// Intentional two-step enrollment for a signed-in browser account (B6): confirm the password (a
// Google-only session has none, so it skips this), add the key to an authenticator app (TOTP is the
// only second factor; never SMS or email), then prove it with a code. Nothing is called enrolled until the
// server verifies that code. Enrollment signs the account out everywhere (including a phone in
// use), so the page blocks finishing during a call and always ends in a fresh sign-in.
import type { DataEnvelope } from '../lib/api.ts';
import { isAbortError } from '../lib/api.ts';
import { type PreparedMfa, OTP, PASSWORD_MAX, parseEnrolled, parsePrepared } from './contract.ts';
import { type FlowDiagnostic, type FlowOptions, Flow, classifyFailure, diagnosticOf, loadIdentityCapabilities } from './flow.ts';
import { APP_NAME } from '../config/brand.ts';

export type MfaPhase =
  | 'idle'
  | 'loading'
  | 'on' // this session already proved a code: two-step verification is set up
  | 'unavailable' // not offered by this server or not allowed for this session
  | 'off' // can be set up
  | 'password'
  | 'preparing'
  | 'enroll' // key shown, waiting for a code
  | 'confirming'
  | 'enrolled' // verified by the server; a fresh sign-in is required
  | 'unknown'
  | 'account_changed'
  | 'error';

export type MfaNotice = 'step_expired' | 'session_changed' | 'too_many_codes' | null;

export interface MfaSnapshot {
  phase: MfaPhase;
  /** Only while enrolling. */
  mfa: PreparedMfa | null;
  stepExpiresAt: string | null;
  notice: MfaNotice;
  fieldError: string | null;
  attempts: number;
  diagnostic: FlowDiagnostic | null;
}

const MAX_CODE_ATTEMPTS = 5;
const STEP_SLACK_MS = 2_000;
const INITIAL: MfaSnapshot = { phase: 'idle', mfa: null, stepExpiresAt: null, notice: null, fieldError: null, attempts: 0, diagnostic: null };

/**
 * Sent in place of a password by a session signed in with Google only: there is no password to
 * confirm, so the server checks the session's Google sign-in instead.
 */
export const GOOGLE_ONLY_SESSION = '!google-only-session!';

export class MfaEnrollFlow extends Flow<MfaSnapshot> {
  private challenge: string | null = null;
  private stepTimer: unknown = null;
  private readonly alreadyOn: boolean;
  /** False for a Google-only session: setup goes straight to the key, with no password step. */
  readonly requiresPassword: boolean;
  private readonly onSessionProblem: () => void;

  constructor(options: FlowOptions & { alreadyOn: boolean; requiresPassword?: boolean; onSessionProblem?: () => void }) {
    super(options, INITIAL);
    this.alreadyOn = options.alreadyOn;
    this.requiresPassword = options.requiresPassword ?? true;
    this.onSessionProblem = options.onSessionProblem ?? (() => undefined);
  }

  /** "Set up two-step verification": ask for the password first, or fetch the key right away for a Google-only session. */
  start(): void {
    if (this.getSnapshot().phase !== 'off') return;
    this.set({ phase: 'password', notice: null, fieldError: null, diagnostic: null });
    if (!this.requiresPassword) void this.prepare(GOOGLE_ONLY_SESSION);
  }

  /** Cancel: the challenge and the key are dropped; nothing changed on the account. */
  abandon(): void {
    this.cancel();
    const phase = this.getSnapshot().phase;
    if (phase === 'password' || phase === 'preparing' || phase === 'enroll') {
      this.dropChallenge();
      this.set({ phase: 'off', mfa: null, notice: null, fieldError: null, attempts: 0 });
    }
  }

  retry(): void {
    if (this.getSnapshot().phase === 'error') this.load();
  }

  wipe(): void {
    this.cancel();
    this.dropChallenge();
  }

  protected onAttach(): void {
    const phase = this.getSnapshot().phase;
    if (phase === 'idle' || (phase === 'loading' && !this.busy)) this.load();
    else if ((phase === 'preparing' || phase === 'confirming') && !this.busy) this.set({ phase: phase === 'preparing' ? 'password' : 'enroll' });
  }

  protected onSessionReady(): void {
    if (this.getSnapshot().phase === 'loading' && !this.busy) this.load();
  }

  protected onAccountChanged(): void {
    const phase = this.getSnapshot().phase;
    this.dropChallenge();
    if (phase === 'enrolled' || phase === 'unknown') return;
    this.set({ ...INITIAL, phase: 'account_changed' });
  }

  protected onSessionRotated(): void {
    const phase = this.getSnapshot().phase;
    if (phase === 'enroll' || phase === 'preparing') {
      this.cancel();
      this.dropChallenge();
      this.set({ phase: 'password', mfa: null, notice: 'session_changed', fieldError: null, attempts: 0 });
    }
  }

  private load(): void {
    if (this.alreadyOn) {
      this.set({ phase: 'on' });
      return;
    }
    this.set({ phase: 'loading', diagnostic: null });
    if (!this.sessionReady || !this.attached) return;
    void this.check();
  }

  private async check(): Promise<void> {
    const { gen, signal } = this.begin();
    try {
      const caps = await loadIdentityCapabilities(this.api, signal);
      if (!this.landed(gen)) return;
      if (caps.kind === 'missing') return this.set({ phase: 'unavailable', diagnostic: caps.diagnostic });
      if (caps.kind === 'error') return this.set({ phase: 'error', diagnostic: caps.diagnostic });
      const mfa = caps.value.mfa;
      if (mfa.status !== 'enabled' || !mfa.allowed) return this.set({ phase: 'unavailable', diagnostic: { code: mfa.reason_code ?? (mfa.allowed ? 'mfa_unconfigured' : 'mfa_not_allowed'), requestId: null } });
      this.set({ phase: 'off', diagnostic: null });
    } catch (error) {
      if (isAbortError(error) || !this.landed(gen)) return;
      this.set({ phase: 'error', diagnostic: diagnosticOf(error) });
    }
  }

  async prepare(password: string): Promise<void> {
    if (this.getSnapshot().phase !== 'password' || !this.sessionReady) return;
    if (password.length === 0 || password.length > PASSWORD_MAX) {
      this.set({ fieldError: 'Enter your current password.' });
      return;
    }
    const { gen, signal } = this.begin();
    this.set({ phase: 'preparing', fieldError: null, notice: null, diagnostic: null });
    try {
      const env = await this.api.request<DataEnvelope<unknown>>('/identity/mfa/prepare', { method: 'POST', body: JSON.stringify({ password }), signal });
      if (!this.landed(gen)) return;
      const prepared = parsePrepared(env?.data);
      if (!prepared || !prepared.mfa.enrollment_required || !prepared.mfa.secret) {
        this.set({ phase: 'password', fieldError: 'The server’s answer wasn’t usable, so nothing changed. Try again.', diagnostic: { code: 'invalid_response', requestId: null } });
        return;
      }
      this.challenge = prepared.challenge;
      this.set({ phase: 'enroll', mfa: prepared.mfa, stepExpiresAt: prepared.expires_at, attempts: 0 });
      this.armStepTimer(prepared.expires_at);
    } catch (error) {
      if (isAbortError(error) || !this.landed(gen)) return;
      const kind = classifyFailure(error);
      const diagnostic = diagnosticOf(error);
      if (kind === 'session') this.onSessionProblem();
      if (kind === 'unavailable') return this.set({ phase: 'unavailable', diagnostic });
      const fieldError =
        kind === 'invalid' ? (this.requiresPassword ? 'That password didn’t match, or two-step verification is already set up for your account.' : 'Your Google sign-in couldn’t be confirmed for setup, or two-step verification is already set up for your account.')
          : kind === 'limited' ? 'Too many attempts. Wait a while, then try again.'
            : kind === 'session' ? (this.requiresPassword ? 'Your browser session was refreshed. Enter your password again.' : 'Your browser session was refreshed. Try again.')
              : kind === 'unknown' || kind === 'network' ? 'Couldn’t reach the server. Check your connection, then try again.'
                : 'That didn’t work. Try again.';
      this.set({ phase: 'password', fieldError, diagnostic });
    }
  }

  async confirm(code: string): Promise<void> {
    const snap = this.getSnapshot();
    if (snap.phase !== 'enroll' || !this.challenge || !this.sessionReady) return;
    if (!OTP.test(code)) {
      this.set({ fieldError: `Enter the 6-digit code your authenticator app shows for ${APP_NAME}.` });
      return;
    }
    const { gen, signal } = this.begin();
    this.set({ phase: 'confirming', fieldError: null, diagnostic: null });
    try {
      const env = await this.api.request<DataEnvelope<unknown>>('/identity/mfa/confirm', { method: 'POST', body: JSON.stringify({ challenge: this.challenge, mfa_code: code }), signal });
      if (!this.landed(gen)) return;
      this.dropChallenge();
      this.set(parseEnrolled(env?.data) ? { phase: 'enrolled', mfa: null } : { phase: 'unknown', mfa: null, diagnostic: { code: 'invalid_response', requestId: null } });
    } catch (error) {
      if (isAbortError(error) || !this.landed(gen)) return;
      const kind = classifyFailure(error);
      const diagnostic = diagnosticOf(error);
      if (kind === 'session') this.onSessionProblem();
      if (kind === 'unknown') {
        this.dropChallenge();
        return this.set({ phase: 'unknown', mfa: null, diagnostic });
      }
      if (kind === 'unavailable') {
        this.dropChallenge();
        return this.set({ phase: 'unavailable', mfa: null, diagnostic });
      }
      if (kind === 'session') {
        this.dropChallenge();
        return this.set({ phase: 'password', mfa: null, notice: 'session_changed', attempts: 0, diagnostic });
      }
      if (kind === 'invalid') {
        const attempts = snap.attempts + 1;
        if (attempts >= MAX_CODE_ATTEMPTS) {
          this.dropChallenge();
          return this.set({ phase: 'password', mfa: null, notice: 'too_many_codes', attempts: 0, diagnostic });
        }
        return this.set({ phase: 'enroll', attempts, fieldError: 'That code didn’t work. Check that your device’s clock is right and enter the current code.', diagnostic });
      }
      this.set({ phase: 'enroll', fieldError: kind === 'limited' ? 'Too many attempts. Wait a while, then try again.' : 'That didn’t work. Try again.', diagnostic });
    }
  }

  private armStepTimer(expiresAt: string): void {
    if (this.stepTimer !== null) this.clearTimer(this.stepTimer);
    const ms = Math.max(0, Date.parse(expiresAt) - this.now() - STEP_SLACK_MS);
    this.stepTimer = this.setTimer(() => {
      this.stepTimer = null;
      if (this.getSnapshot().phase !== 'enroll') return;
      this.dropChallenge();
      this.set({ phase: 'password', mfa: null, notice: 'step_expired', fieldError: null, attempts: 0 });
    }, Math.min(ms, 2 ** 31 - 1));
  }

  private dropChallenge(): void {
    if (this.stepTimer !== null) this.clearTimer(this.stepTimer);
    this.stepTimer = null;
    this.challenge = null;
    if (this.getSnapshot().mfa) this.set({ mfa: null, stepExpiresAt: null });
  }
}
